import React from 'react'
import { useNavigate } from 'react-router-dom'
import _ from 'underscore'
import styled from 'styled-components'
import EditIcon from '@mui/icons-material/Edit'

import AddButton from 'components/AddButton'
import Table from 'components/Table'

import StatusCell from 'pages/shared/StatusCell'

import useGroups from 'hooks/useGroups'
import { useStoreContext } from 'hooks/StoreContext'

import { color } from 'styles/colors'
import { LoadingSinglePanePage, screenSize, Circle } from 'styles/shared'

import { generateUniqueId } from 'helpers/formatters'

const Groups = () => {
  const { groups, areGroupsLoaded } = useGroups()
  const { deviceMetrics, areDeviceMetricsLoaded } = useStoreContext()

  const navigate = useNavigate()

  if (!areGroupsLoaded || !areDeviceMetricsLoaded) {
    return <LoadingSinglePanePage />
  }

  return (
    <>
      <S.Header>
        <AddButton uri="/admin/group/new" testId="Add Group">Add Group</AddButton>
      </S.Header>
      <Table
        items={groups}
        noDataMessage={{
          title: 'No Group Metric',
          description: 'click Add Group to add a group metric'
        }}
      >
        <S.TableHeader>
          <Table.HeaderCell title="Name" />
          <Table.HeaderCell title="Unit" />
          <Table.HeaderCell title="Description" />
          <Table.HeaderCell title="Call to Actions" />
          <Table.HeaderCell title="Metrics" />
          <Table.HeaderCell title="Range" />
          <Table.HeaderCell title="Draft Status" />
        </S.TableHeader>
        {groups.map(({ groupId, name, unit, description, callToAction, metrics, range, draft }) => (
          <S.Row key={name}>
            <S.Cell>
              <S.MetricId>{name}</S.MetricId>
            </S.Cell>
            <S.Cell>
              <S.MetricId>{unit}</S.MetricId>
            </S.Cell>
            <S.Cell>
              <S.MetricId>{description}</S.MetricId>
            </S.Cell>
            <S.Cell>
              <S.List itemsCount={callToAction?.length ?? 0}>
                {callToAction?.length > 0
                  ? callToAction.map(({ description, details }) =>
                    (
                      <div key={generateUniqueId()}>
                        <li>{description}</li>
                        {details && <S.DetailsList> {details.map(detail => <li key={generateUniqueId()}>{detail}</li>)}</S.DetailsList>}
                      </div>
                    )
                  )
                  : '--'}
              </S.List>
            </S.Cell>
            <S.Cell>
              <S.Metrics>
                {_.pluck(metrics, 'metricId').map(id => {
                  const metric = _.find(deviceMetrics, (metric) => metric.id === id)
                  return (
                    <S.Metric key={id}>
                      <Circle color={metric.color} />
                      <div>{metric['name']}</div>
                    </S.Metric>)
                })}
              </S.Metrics>
            </S.Cell>
            <S.Cell>
              <S.MetricId>{range}</S.MetricId>
            </S.Cell>
            <div>
              <StatusCell isEnabled={draft} />
              <EditIcon
                fontSize="small"
                color="primary"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/admin/groups/${groupId}/edit`)}
                aria-label={`Edit Device metric with title: ${name}`}
              >
                Edit
              </EditIcon>
            </div>
          </S.Row>
        ))}
      </Table>
    </>
  )
}

const S = {
  TableRow: styled(Table.Row)`
    justify-content: start;
    align-items: center;
  `,
  Cell: styled(Table.Cell)`
    overflow-wrap: break-word;
  `,
  MetricId: styled.p`
    overflow-wrap: break-word;
  `,
  Header: styled.div`
    display: flex;
    justify-content: end;
    padding-bottom: 5px;
    margin: 20px 10px;

    @media ${screenSize.small} {
      justify-content: end;
    }
  `,
  TableHeader: styled(Table.Header)`
    display: grid;
    grid-template-columns: 15% 5% 18% 18% 15% 15% auto;
    position: sticky;
    top: 15px;
  `,
  Row: styled(Table.Row)`
    display: grid;
    grid-template-columns: 15% 5% 18% 18% 15% 15% auto;
    align-items: center;
    min-height: 50px;
    padding-top: 10px;
    padding-bottom: 10px;
  `,
  StatusCell: styled(Table.Cell)`
    flex: 1;
    flex-direction: column;
    @media ${screenSize.small} {
      display: inline-block;
      width: 50%;
      padding-left: 5px;
    }
  `,
  Status: styled.div.withConfig({
    shouldForwardProp: (prop) => !['enabled'].includes(prop)
  })<{ enabled: boolean }>`
    display: inline-block;
    padding: 4px 10px;
    margin-bottom: 5px;
    text-align: center;
    font-size: 12px;
    letter-spacing: 0;
    text-transform: uppercase;
    border: 1px solid ${color('border')};
    border-radius: 14px;
    margin-right: 10px;
    ${({ enabled }) => {
    if (enabled) {
      return `
        color: ${color('siteMessageStatusDisabledText')};
      `
    } else {
      return `
        border-color: ${color('siteMessageEnabledStatusBorder')};
        background-color: ${color('positiveStatus')};
        color: ${color('siteMessageStatusEnabledText')};
      `
    }
  }}
  `,
  Metrics: styled.div`
    display: flex;
    flex-direction: column;
  `,
  Metric: styled.div`
    display: grid;
    grid-template-columns: 15px auto;
    grid-column-gap: 10px;
    margin-bottom: 10px;
  `,
  List: styled.ul.withConfig({
    shouldForwardProp: (prop) => !['itemsCount'].includes(prop)
  })<{ itemsCount: number }>`
    list-style-type: ${({ itemsCount }) => itemsCount > 1 ? 'disc' : 'none'};
    list-style-position: inside;
    padding-bottom: 10px;
    padding-left: 10px;
    margin: 0;
  `,
  DetailsList: styled.ul`
    list-style-position: inside;
    padding-left: 10px;
  `
}

export default Groups
